import { FieldChange, ExtractedField } from '../types';

/**
 * Edit history entry for tracking field changes over time
 */
export interface EditHistoryEntry {
  fieldId: string;
  fieldName: string;
  previousValue: string;
  newValue: string;
  timestamp: string;
  userId: string;
  userEmail: string;
  changeReason?: string;
  confidence?: number;
}

/**
 * Complete audit trail for a document validation session
 */
export interface DocumentAuditTrail {
  documentId: string;
  sessionId: string;
  startTime: string;
  endTime?: string;
  userId: string;
  userEmail: string;
  editHistory: EditHistoryEntry[];
  finalChangesSummary: FieldChange[];
  validationMetrics: {
    totalFields: number;
    fieldsChanged: number;
    lowConfidenceFields: number;
    timeSpentMinutes: number;
  };
}

/**
 * In-memory edit history tracker for a validation session
 */
class EditHistoryTracker {
  private editHistory: EditHistoryEntry[] = [];
  private sessionStartTime: string;
  private sessionId: string;
  private documentId: string;
  private userId: string;
  private userEmail: string;

  constructor(documentId: string, userId: string, userEmail: string) {
    this.documentId = documentId;
    this.userId = userId;
    this.userEmail = userEmail;
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date().toISOString();
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track a field change
   * Requirements: 4.5 - Implement edit history tracking for audit trail
   */
  trackFieldChange(
    fieldId: string,
    fieldName: string,
    previousValue: string,
    newValue: string,
    confidence?: number,
    changeReason?: string
  ): void {
    // Only track if values are actually different
    if (previousValue !== newValue) {
      const entry: EditHistoryEntry = {
        fieldId,
        fieldName,
        previousValue,
        newValue,
        timestamp: new Date().toISOString(),
        userId: this.userId,
        userEmail: this.userEmail,
        confidence,
        changeReason,
      };

      this.editHistory.push(entry);
    }
  }

  /**
   * Get all edit history entries for this session
   */
  getEditHistory(): EditHistoryEntry[] {
    return [...this.editHistory];
  }

  /**
   * Get edit history for a specific field
   */
  getFieldEditHistory(fieldId: string): EditHistoryEntry[] {
    return this.editHistory.filter(entry => entry.fieldId === fieldId);
  }

  /**
   * Get the latest value for a field based on edit history
   */
  getLatestFieldValue(fieldId: string, originalValue: string): string {
    const fieldHistory = this.getFieldEditHistory(fieldId);
    
    if (fieldHistory.length === 0) {
      return originalValue;
    }

    // Return the newest value (last entry)
    return fieldHistory[fieldHistory.length - 1].newValue;
  }

  /**
   * Create a complete audit trail for the session
   */
  createAuditTrail(
    originalFields: ExtractedField[],
    finalValidatedFields: Record<string, string>
  ): DocumentAuditTrail {
    const endTime = new Date().toISOString();
    const startTime = new Date(this.sessionStartTime);
    const endTimeObj = new Date(endTime);
    const timeSpentMinutes = Math.round((endTimeObj.getTime() - startTime.getTime()) / (1000 * 60));

    // Create final changes summary
    const finalChangesSummary: FieldChange[] = [];
    let fieldsChanged = 0;
    let lowConfidenceFields = 0;

    originalFields.forEach(field => {
      const finalValue = finalValidatedFields[field.id] || '';
      
      if (field.value !== finalValue) {
        fieldsChanged++;
        finalChangesSummary.push({
          fieldId: field.id,
          fieldName: field.name,
          originalValue: field.value,
          newValue: finalValue,
          confidence: field.confidence,
        });
      }

      if (field.confidence < 80) {
        lowConfidenceFields++;
      }
    });

    return {
      documentId: this.documentId,
      sessionId: this.sessionId,
      startTime: this.sessionStartTime,
      endTime,
      userId: this.userId,
      userEmail: this.userEmail,
      editHistory: this.getEditHistory(),
      finalChangesSummary,
      validationMetrics: {
        totalFields: originalFields.length,
        fieldsChanged,
        lowConfidenceFields,
        timeSpentMinutes,
      },
    };
  }

  /**
   * Clear edit history (useful for testing or resetting)
   */
  clearHistory(): void {
    this.editHistory = [];
  }

  /**
   * Get session statistics
   */
  getSessionStats(): {
    totalEdits: number;
    fieldsModified: number;
    sessionDurationMinutes: number;
  } {
    const uniqueFields = new Set(this.editHistory.map(entry => entry.fieldId));
    const currentTime = new Date();
    const startTime = new Date(this.sessionStartTime);
    const durationMinutes = Math.round((currentTime.getTime() - startTime.getTime()) / (1000 * 60));

    return {
      totalEdits: this.editHistory.length,
      fieldsModified: uniqueFields.size,
      sessionDurationMinutes: durationMinutes,
    };
  }
}

/**
 * Factory function to create edit history tracker
 */
export const createEditHistoryTracker = (
  documentId: string,
  userId: string,
  userEmail: string
): EditHistoryTracker => {
  return new EditHistoryTracker(documentId, userId, userEmail);
};

/**
 * Utility functions for working with audit trails
 */
export const auditTrailUtils = {
  /**
   * Serialize audit trail for storage or transmission
   */
  serializeAuditTrail(auditTrail: DocumentAuditTrail): string {
    return JSON.stringify(auditTrail, null, 2);
  },

  /**
   * Deserialize audit trail from storage
   */
  deserializeAuditTrail(serialized: string): DocumentAuditTrail {
    return JSON.parse(serialized);
  },

  /**
   * Create a summary report of changes
   */
  createChangesSummaryReport(auditTrail: DocumentAuditTrail): string {
    const { validationMetrics, finalChangesSummary } = auditTrail;
    
    let report = `Validation Summary for Document ${auditTrail.documentId}\n`;
    report += `Session Duration: ${validationMetrics.timeSpentMinutes} minutes\n`;
    report += `Total Fields: ${validationMetrics.totalFields}\n`;
    report += `Fields Changed: ${validationMetrics.fieldsChanged}\n`;
    report += `Low Confidence Fields: ${validationMetrics.lowConfidenceFields}\n\n`;

    if (finalChangesSummary.length > 0) {
      report += `Field Changes:\n`;
      finalChangesSummary.forEach(change => {
        report += `- ${change.fieldName}: "${change.originalValue}" → "${change.newValue}" (confidence: ${change.confidence}%)\n`;
      });
    } else {
      report += `No changes were made to the document.\n`;
    }

    return report;
  },

  /**
   * Validate audit trail completeness
   */
  validateAuditTrail(auditTrail: DocumentAuditTrail): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!auditTrail.documentId) {
      errors.push('Document ID is missing');
    }

    if (!auditTrail.sessionId) {
      errors.push('Session ID is missing');
    }

    if (!auditTrail.userId) {
      errors.push('User ID is missing');
    }

    if (!auditTrail.startTime) {
      errors.push('Start time is missing');
    }

    if (auditTrail.editHistory.length === 0 && auditTrail.finalChangesSummary.length > 0) {
      errors.push('Final changes exist but no edit history recorded');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

// Export the EditHistoryTracker class for direct use if needed
export { EditHistoryTracker };